import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import c from 'classnames';

import { urls } from '@repo/app-constants';
import useSubscriptions from 'services/general/subscriptionsList';
import useSignOut from 'services/auth/useSignOut';
import { usePortalConfigs } from 'hooks/usePortalConfigs';
import { getFullName, getAccountEmail } from 'store/account';

import {
  Button,
  ButtonColors,
  ButtonSizes,
  ButtonVariants,
  LinkButton,
  Typography,
} from 'components';
import AccountNameModal from './AccountNameModal';
import AccountEmailModal from './AccountEmailModal';
import AccountField from './AccountField/AccountField';
import { Practitioners } from './Practitioners/Practitioners';

import styles from './Account.module.scss';

const Account = () => {
  const navigate = useNavigate();
  const signOut = useSignOut();
  const fullName = useSelector(getFullName);
  const email = useSelector(getAccountEmail);
  const [showModalName, setShowModalName] = useState<boolean>(false);
  const [showModalEmail, setShowModalEmail] = useState<boolean>(false);
  const { isAnyTinyPlus, isTinyPlus } = useSubscriptions();
  const { config: portalConfig, isLoading: isPortalConfigLoading } =
    usePortalConfigs();

  const editName = () => {
    setShowModalName(true);
  };
  const editEmail = () => {
    setShowModalEmail(true);
  };

  const viewVersionLog = () => {
    window.open(urls.VERSION_LOG, '_blank');
  };
  const viewSubscriptions = () => {
    navigate(`/account/subscriptions`);
  };
  const toggleModalName = () => setShowModalName(prev => !prev);
  const toggleModalEmail = () => setShowModalEmail(prev => !prev);

  return (
    <>
      {fullName && (
        <AccountNameModal toggle={toggleModalName} showModal={showModalName} />
      )}
      {email && (
        <AccountEmailModal
          toggle={toggleModalEmail}
          showModal={showModalEmail}
        />
      )}
      <div className={styles.accountContainer}>
        <Typography variant='heading-xl' className='mb-4'>
          Account
        </Typography>
        <div className={styles.section}>
          <AccountField title='Name' action={editName} actionName='Edit'>
            <span className={styles.value}>{fullName}</span>
          </AccountField>
          <AccountField title='Email' action={editEmail} actionName='Edit'>
            <span className={styles.value}>{email}</span>
          </AccountField>
        </div>

        {!portalConfig.hide_upsells && !isPortalConfigLoading && (
          <div className={styles.section}>
            <AccountField
              title={'Membership'}
              action={isAnyTinyPlus ? viewSubscriptions : undefined}
              actionName={isAnyTinyPlus ? 'Manage' : ''}
            >
              {isAnyTinyPlus ? (
                <div className='flex flex-column'>
                  <span className={styles.value}>Tiny+ member</span>
                  <span className={c(styles.value, 'mt-2')}>
                    {`Access your Tiny+ members-only rate of $${
                      isTinyPlus ? 149 : 169
                    } per kit: `}
                    <LinkButton
                      label='Gut kit'
                      href={
                        isTinyPlus
                          ? urls.CHECKOUT_WITH_KIT_TINY_PLUS
                          : urls.CHECKOUT_WITH_KIT_TINY_PLUS_PROGRAM_OR_PROACTIVE +
                            '&utm_source=tinyhealth&utm_medium=webapp&utm_campaign=accountpage'
                      }
                      external
                      variant={ButtonVariants.TEXT}
                      size={ButtonSizes.M}
                      color={ButtonColors.PURPLE}
                    />
                    {`, `}
                    <LinkButton
                      label='Vaginal kit.'
                      href={
                        isTinyPlus
                          ? urls.CHECKOUT_WITH_KIT_TINY_PLUS_VAGINAL
                          : urls.CHECKOUT_WITH_KIT_TINY_PLUS_PROGRAM_OR_PROACTIVE_VAGINAL +
                            '&utm_source=tinyhealth&utm_medium=webapp&utm_campaign=accountpage'
                      }
                      external
                      variant={ButtonVariants.TEXT}
                      size={ButtonSizes.M}
                      color={ButtonColors.PURPLE}
                    />
                  </span>
                </div>
              ) : (
                <div className='flex flex-column'>
                  <div className={styles.value}>Basic </div>
                  <LinkButton
                    label='Learn more about Tiny+ memberships'
                    href={urls.STORE}
                    external
                    variant={ButtonVariants.TEXT}
                    size={ButtonSizes.M}
                    color={ButtonColors.PURPLE}
                  />
                </div>
              )}
            </AccountField>
          </div>
        )}

        <div className={styles.section}>
          <AccountField title='Practitioner'>
            <div className='flex flex-column'>
              <span className={styles.value}>
                {`If your practitioner is a Tiny Health Member you can add them here, This will give them access to your results, surveys and action plan.`}
              </span>
              <Practitioners />
            </div>
          </AccountField>
        </div>
        <div className={styles.section}>
          <AccountField
            title='Version Log'
            action={viewVersionLog}
            actionName={'View'}
          >
            <span className={styles.value}>
              Email hello@tinyhealth.com to update to the latest system version.
            </span>
          </AccountField>
        </div>
        <div className='mt-auto pt-4'>
          <Button
            label='Sign Out'
            size={ButtonSizes.M}
            variant={'outline'}
            width={'full'}
            iconNameLeft='logOut'
            onClick={signOut}
          />
        </div>
      </div>
    </>
  );
};

export default Account;
